import * as React from "react";
import { Container, Header, Segment } from "semantic-ui-react";
import { createValidator } from "not-valid";

import { AnsweredQuestionWithId, QuestionnaireTypes } from "@teamprevent/common/questionnaires/model";
import { QuestionnaireConfig } from "@teamprevent/common/questionnaires/questionnaireConfig";
import { OldProgressBarQuestionnaireContainer } from "@teamprevent/common/questionnaires/components/OldProgressBarQuestionnaireContainer";
import { OldProgressBarQuestionnaireSection } from "@teamprevent/common/questionnaires/components/OldProgressBarQuestionnaireSection";
import { TextQuestion } from "@teamprevent/common/questionnaires/components/questions/TextQuestion";
import { DropdownQuestion } from "@teamprevent/common/questionnaires/components/questions/DropdownQuestion";
import { CheckboxQuestion } from "@teamprevent/common/questionnaires/components/questions/CheckboxQuestion";
import * as questionIds from "@teamprevent/common/questionnaires/questionnaireConfig/sraManagerSection/questionIds";
import { CustomerSraDepartment } from "@teamprevent/common/customerSraDepartment/model";

interface Props {
    readonly: boolean;
    questionnaireConfig: QuestionnaireConfig;
    submitQuestionnaire?: (answers: AnsweredQuestionWithId[], questionnaireKey: string) => Promise<boolean>;
    submittingQuestionnaire: boolean;
    loadQuestionnaire?: () => void;
    customerSraDepartments?: CustomerSraDepartment[];
    questions: AnsweredQuestionWithId[];
}

export const QUESTIONNAIRE_KEY = "SraManagerForm";

export const StressRiskAssessmentManagerForm: React.FunctionComponent<Props> = (props: Props) => {

    const submitQuestionnaire = async (answers: AnsweredQuestionWithId[]): Promise<boolean> => {
        const submitted = await props.submitQuestionnaire(answers, QUESTIONNAIRE_KEY);
        return submitted;
    };

    const getQuestionConfig = (questionId: string) => {
        return props.questionnaireConfig.questions.find(c => c.id === questionId) || { id: questionId, disabled: true };
    };

    const getAnswerToQuestionWithId = (questionId: string) => {
        return props.questions.find(question => question.id === questionId)
            && props.questions.find(question => question.id === questionId).answer;
    };

    const validateConfirmEmailAddress =
        createValidator((value) => getAnswerToQuestionWithId("PersonalEmailAddress") === value, "Email addresses do not match");

    return (
        <>
            <Container className="ppq-form">
                <Container>
                    <Segment basic>
                        <Header as="h1">Stress Risk Assessment</Header>
                    </Segment>
                </Container>
                <OldProgressBarQuestionnaireContainer
                    loadQuestionnaire={props.loadQuestionnaire}
                    submitQuestionnaire={submitQuestionnaire}
                    readonly={props.readonly}
                    questionnaireKey={QUESTIONNAIRE_KEY}
                    questionnaireType={QuestionnaireTypes.Other}
                >
                    <OldProgressBarQuestionnaireSection
                        title={"Stress Risk Assessment"}
                        id={"StressRiskAssessment"}
                    >
                        <>
                            <TextQuestion
                                questionConfig={getQuestionConfig(questionIds.ReferredTitle)}
                                readonly={props.readonly}
                                mandatory
                            />
                            <TextQuestion
                                questionConfig={getQuestionConfig(questionIds.ReferredFirstName)}
                                readonly={props.readonly}
                                mandatory
                            />
                            <TextQuestion
                                questionConfig={getQuestionConfig(questionIds.ReferredLastName)}
                                readonly={props.readonly}
                                mandatory
                            />
                            <TextQuestion
                                questionConfig={getQuestionConfig(questionIds.EmployeeId)}
                                readonly={props.readonly}
                                mandatory
                            />
                            <TextQuestion
                                questionConfig={getQuestionConfig(questionIds.JobTitle)}
                                readonly={props.readonly}
                                mandatory
                            />
                            <DropdownQuestion
                                mandatory
                                questionConfig={getQuestionConfig(questionIds.DepartmentId)}
                                options={props.customerSraDepartments &&
                                    props.customerSraDepartments.map(department => ({ text: department.name, value: department.id }))}
                                search
                                readonly={props.readonly}
                            />
                            <TextQuestion
                                questionConfig={getQuestionConfig(questionIds.PersonalEmailAddress)}
                                readonly={props.readonly}
                                mandatory
                            />
                            <TextQuestion
                                questionConfig={getQuestionConfig(questionIds.EmailAddressConfirmed)}
                                readonly={props.readonly}
                                mandatory
                                validators={[validateConfirmEmailAddress]}
                                validationDependencies={[getAnswerToQuestionWithId("PersonalEmailAddress") as string]}
                            />
                            <CheckboxQuestion
                                questionConfig={getQuestionConfig(questionIds.IndividualAwareAccessPrivacyStatementConsent)}
                                /* eslint-disable-next-line max-len */
                                value="I can confirm that I have made the individual I am inviting to complete this risk assessment aware that I am providing Optima Health with their personal information listed ABOVE"
                                /* eslint-enable-next-line max-len */
                                readonly={props.readonly}
                            />
                            <CheckboxQuestion
                                questionConfig={getQuestionConfig(questionIds.IndividualAwareOfInviteConsent)}
                                /* eslint-disable-next-line max-len */
                                value="I have provided the individual above with access to the Optima Health Privacy Statement which clearly explains how, where and when their personal information and health data will be processed"
                                /* eslint-enable-next-line max-len */
                                readonly={props.readonly}
                            />
                            <CheckboxQuestion
                                questionConfig={getQuestionConfig(questionIds.IndividualAwareRequestAccessInWritingConsent)}
                                /* eslint-disable-next-line max-len */
                                value="I understand that all the information about the individual that is collected during the risk assessment process will be held securely and if the individual wishes to gain access to any information that Optima Health hold about them they can do so by requesting"
                                /* eslint-enable-next-line max-len */
                                readonly={props.readonly}
                            />
                        </>
                    </OldProgressBarQuestionnaireSection>
                </OldProgressBarQuestionnaireContainer>
            </Container>
        </>);
};
